// Libs
import uniqBy from 'lodash/uniqBy';
import sum from 'lodash/sum';
import isEqual from 'lodash/isEqual';
// App
import { ProductDetails, ShopProductSummary } from 'app-shared/lib/api/catalog/product';
import { ShopDetails, ShopPlaceSummary } from 'app-shared/lib/api/catalog/shop';
import { Product, Order, ProductOption } from './bag.slice.interfaces';
import { checkIfCanOrderProduct as checkIfCanOrderProductAPI } from 'app-shared/lib/api/order-processing/order';
import { DialogService, Typography, defaultTheme as theme } from 'ui-components';
import { AxiosError, AxiosResponse } from 'axios';
import { isErrorContainingKey } from 'app-shared/lib/api/utils/axios-request.utils';
import { BagState } from './bag.slice';
import { buildBagProduct, buildBagShop } from './bag.slice.builders';
import { autoSelectOrderTypeIfPossible, getEditableOrderTypes } from './bag.slice.order-type.utils';
import { SelectableProductOptionFormData } from 'app-shared/lib/fragments/product/options-groups';

export const addNewOrder = (shop: ShopDetails): Order => {
  const shopPlaces: ShopPlaceSummary[] = uniqBy(
    shop.openingIntervals
      .map((openingInterval) => openingInterval.place)
      .filter((place: ShopPlaceSummary) => place.id != null),
    'id'
  );
  const editableTypes = getEditableOrderTypes(shop);
  const autoSelectedOrderType = autoSelectOrderTypeIfPossible(editableTypes);

  const newOrder: Order = {
    shop: buildBagShop(shop),
    products: [],
    hasUserSelectedSuggestedProducts: false,
    receptionMeetingStartDate: null,
    receptionMeetingEndDate: null,
    shouldUserSelectMeetingDate: !shop.isDeliveryExternal,
    type: autoSelectedOrderType,
    editableTypes,
    places: shopPlaces,
  };

  return newOrder;
};

export const addNewProduct = (
  order: Order,
  product: ProductDetails | ShopProductSummary,
  selectedOptionsInForm?: SelectableProductOptionFormData[],
  isAiSuggestion?: boolean
): Product => {
  const newProduct = buildBagProduct(product, selectedOptionsInForm, isAiSuggestion);

  order.products.push(newProduct);

  return newProduct;
};

export const handleCheckIfCanOrderProductErrors = (error: AxiosError): void => {
  if (isErrorContainingKey(error, 'product_out_of_stock')) {
    DialogService.showInfo({
      title: 'Produit en rupture de stock',
      body: <Typography>Ce produit a été victime de son succès, vous ne pouvez donc plus le commander !</Typography>,
      theme,
    });
  } else if (isErrorContainingKey(error, 'product_unavailable')) {
    DialogService.showInfo({
      title: 'Produit indisponible',
      body: <Typography>Vous ne pouvez pas commander ce produit car le restaurant a suspendu sa vente.</Typography>,
      theme,
    });
  }
};

export const checkIfCanOrderProduct = async (state: BagState, id: number): Promise<boolean> => {
  const products = findProductsById(state, id);
  const minQuantity = products[0]?.minQuantity;
  const maxQuantity = products[0]?.maxQuantity;
  const hasMaxQuantity = maxQuantity != null;
  const quantityAlreadyInBag = sum(products.map((product: Product) => product.quantity)) || 0;
  const desiredQuantity = minQuantity || quantityAlreadyInBag + 1;
  const hasReachedMaxQuantity = hasMaxQuantity && quantityAlreadyInBag >= maxQuantity;

  if (hasReachedMaxQuantity) {
    DialogService.showInfo({
      title: 'Quantité maximale atteinte',
      body: 'Vous avez atteint la quantité maximale de ce produit.',
      theme,
    });

    return false;
  }

  const response: AxiosResponse<boolean> = (await checkIfCanOrderProductAPI(id, desiredQuantity).catch(
    handleCheckIfCanOrderProductErrors
  )) as AxiosResponse<boolean>;

  return response?.data;
};

export const findProductExactMatch = (
  state: BagState,
  product: ProductDetails | ShopProductSummary,
  selectedOptions?: SelectableProductOptionFormData[]
): Product => {
  if (!state.order) return null;

  const foundProduct = state.order.products.find((productInBag: Product) => {
    const areIdsEquals = productInBag.id == product.id;
    const productInBagSelectedOptionsIds = productInBag.selectedOptions?.map((option) => option.id);
    const productSelectedOptionsIds = selectedOptions?.map((option) => option.id);
    const areSelectedOptionsEquals = isEqual(productInBagSelectedOptionsIds, productSelectedOptionsIds);

    return areIdsEquals && areSelectedOptionsEquals;
  });

  return foundProduct;
};

// A single product id can map to several lines when their selected options differ.
export const findProductsById = (state: BagState, id: number): Product[] => {
  if (!state.order) return [];

  return state.order.products.filter((product: Product) => product.id == id);
};

export const computeProductTotal = (product: Product): number => {
  const productOptionsTotals = product.selectedOptions?.map((option) => computeProductOptionTotal(option));

  return product.quantity * (product.price + sum(productOptionsTotals));
};

export const computeProductOptionTotal = (productOption: ProductOption): number => {
  return productOption.quantity * productOption.priceSupplement;
};
